import { useCallback, useState } from "react";

import {
  getOfferingsApi,
  getOfferingApi,
  createOfferingApi,
  updateOfferingApi,
  deleteOfferingApi,

  getOfferingImagesApi,
  createOfferingImageApi,
  updateOfferingImageApi,
  deleteOfferingImageApi
} from "../../../api/offering/offering.api";


/*
|--------------------------------------------------------------------------
| PRICING PLAN API
|--------------------------------------------------------------------------
*/

import {
  getPricingPlansApi,
  createPricingPlanApi,
  updatePricingPlanApi,
  deletePricingPlanApi
} from "../../../api/pricing-plans/pricing-plans.api";


const useOfferings = () => {

  const [offerings, setOfferings] =
    useState([]);

  const [offering, setOffering] =
    useState(null);

  const [images, setImages] =
    useState([]);

  const [pricingPlans, setPricingPlans] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [error, setError] =
    useState(null);


  /*
  |--------------------------------------------------------------------------
  | FETCH OFFERINGS
  |--------------------------------------------------------------------------
  */

  const fetchOfferings = useCallback(
    async (params = {}) => {

      try {

        setLoading(true);
        setError(null);

        const response =
          await getOfferingsApi(params);

        const data =
          response?.data?.data ??
          response?.data ??
          [];

        const normalizedData =
          Array.isArray(data)
            ? data
            : [];

        setOfferings(
          normalizedData
        );

        return normalizedData;

      } catch (err) {

        console.error(
          "Failed to fetch offerings:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to fetch offerings"
        );

        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | FETCH SINGLE OFFERING
  |--------------------------------------------------------------------------
  */

  const fetchOffering = useCallback(
    async (id) => {

      if (!id) {
        return null;
      }

      try {

        setDetailsLoading(true);
        setError(null);

        const response =
          await getOfferingApi(id);

        const data =
          response?.data?.data ??
          response?.data ??
          null;

        setOffering(data);

        return data;

      } catch (err) {

        console.error(
          "Failed to fetch offering:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to fetch offering"
        );

        throw err;

      } finally {

        setDetailsLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CREATE OFFERING
  |--------------------------------------------------------------------------
  */

  const createOffering = useCallback(
    async (data) => {

      try {

        setLoading(true);
        setError(null);

        const response =
          await createOfferingApi(data);

        const result =
          response?.data?.data ??
          response?.data;

        return result;

      } catch (err) {

        console.error(
          "Failed to create offering:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to create offering"
        );

        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | UPDATE OFFERING
  |--------------------------------------------------------------------------
  */

  const updateOffering = useCallback(
    async (id, data) => {

      try {

        setLoading(true);
        setError(null);

        const response =
          await updateOfferingApi(
            id,
            data
          );

        const result =
          response?.data?.data ??
          response?.data;

        setOffering(result);

        return result;

      } catch (err) {

        console.error(
          "Failed to update offering:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to update offering"
        );

        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | DELETE OFFERING
  |--------------------------------------------------------------------------
  */

  const removeOffering = useCallback(
    async (id) => {

      if (!id) {
        return;
      }

      try {

        setLoading(true);
        setError(null);

        await deleteOfferingApi(id);

        setOfferings(
          (previous) =>
            previous.filter(
              (item) =>
                item.id !== id
            )
        );

      } catch (err) {

        console.error(
          "Failed to delete offering:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to delete offering"
        );

        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | OFFERING IMAGES
  |--------------------------------------------------------------------------
  */

  const fetchImages = useCallback(
    async (offeringId) => {

      if (!offeringId) {
        return [];
      }

      try {

        const response =
          await getOfferingImagesApi(
            offeringId
          );

        const data =
          response?.data?.data ??
          response?.data ??
          [];

        const normalizedData =
          Array.isArray(data)
            ? data
            : [];

        setImages(
          normalizedData
        );

        return normalizedData;

      } catch (err) {

        console.error(
          "Failed to fetch offering images:",
          err
        );

        throw err;

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CREATE IMAGE
  |--------------------------------------------------------------------------
  */

  const createImage = useCallback(
    async (
      offeringId,
      data
    ) => {

      const response =
        await createOfferingImageApi(
          offeringId,
          data
        );

      const result =
        response?.data?.data ??
        response?.data;

      await fetchImages(
        offeringId
      );

      return result;

    },
    [fetchImages]
  );


  /*
  |--------------------------------------------------------------------------
  | UPDATE IMAGE
  |--------------------------------------------------------------------------
  */

  const updateImage = useCallback(
    async (
      offeringId,
      imageId,
      data
    ) => {

      const response =
        await updateOfferingImageApi(
          offeringId,
          imageId,
          data
        );

      const result =
        response?.data?.data ??
        response?.data;

      await fetchImages(
        offeringId
      );

      return result;

    },
    [fetchImages]
  );


  /*
  |--------------------------------------------------------------------------
  | DELETE IMAGE
  |--------------------------------------------------------------------------
  */

  const deleteImage = useCallback(
    async (
      offeringId,
      imageId
    ) => {

      await deleteOfferingImageApi(
        offeringId,
        imageId
      );

      await fetchImages(
        offeringId
      );

    },
    [fetchImages]
  );


  /*
  |--------------------------------------------------------------------------
  | PRICING PLANS
  |--------------------------------------------------------------------------
  */

  const fetchPricingPlans = useCallback(
    async (
      offeringId,
      activeOnly = true
    ) => {

      if (!offeringId) {
        return [];
      }

      try {

        const response =
          await getPricingPlansApi(
            offeringId,
            activeOnly
          );

        const data =
          response?.data?.data ??
          response?.data ??
          [];

        const normalizedData =
          Array.isArray(data)
            ? data
            : [];

        setPricingPlans(
          normalizedData
        );

        return normalizedData;

      } catch (err) {

        console.error(
          "Failed to fetch pricing plans:",
          err
        );

        throw err;

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CREATE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const createPricingPlan = useCallback(
    async (
      offeringId,
      data
    ) => {

      if (!offeringId) {
        throw new Error(
          "Offering ID is required"
        );
      }

      const response =
        await createPricingPlanApi(
          offeringId,
          data
        );

      const result =
        response?.data?.data ??
        response?.data;

      await fetchPricingPlans(
        offeringId
      );

      return result;

    },
    [fetchPricingPlans]
  );


  /*
  |--------------------------------------------------------------------------
  | UPDATE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const updatePricingPlan = useCallback(
    async (
      offeringId,
      planId,
      data
    ) => {

      if (!planId) {
        throw new Error(
          "Pricing plan ID is required"
        );
      }

      const response =
        await updatePricingPlanApi(
          planId,
          data
        );

      const result =
        response?.data?.data ??
        response?.data;

      await fetchPricingPlans(
        offeringId
      );

      return result;

    },
    [fetchPricingPlans]
  );


  /*
  |--------------------------------------------------------------------------
  | DELETE PRICING PLAN
  |--------------------------------------------------------------------------
  */

  const deletePricingPlan = useCallback(
    async (
      offeringId,
      planId
    ) => {

      if (!planId) {
        throw new Error(
          "Pricing plan ID is required"
        );
      }

      await deletePricingPlanApi(
        planId
      );

      await fetchPricingPlans(
        offeringId
      );

    },
    [fetchPricingPlans]
  );


  /*
  |--------------------------------------------------------------------------
  | RETURN
  |--------------------------------------------------------------------------
  */

  return {

    offerings,

    offering,

    images,

    pricingPlans,

    loading,

    detailsLoading,

    error,


    /*
    |----------------------------------------------------------------------
    | OFFERINGS
    |----------------------------------------------------------------------
    */

    fetchOfferings,

    fetchOffering,

    createOffering,

    updateOffering,

    removeOffering,


    /*
    |----------------------------------------------------------------------
    | IMAGES
    |----------------------------------------------------------------------
    */

    fetchImages,

    createImage,

    updateImage,

    deleteImage,


    /*
    |----------------------------------------------------------------------
    | PRICING
    |----------------------------------------------------------------------
    */

    fetchPricingPlans,

    createPricingPlan,

    updatePricingPlan,

    deletePricingPlan,


    /*
    |----------------------------------------------------------------------
    | STATE
    |----------------------------------------------------------------------
    */

    setOffering,

    setOfferings,

    setImages,

    setPricingPlans

  };

};


export default useOfferings;

export {
  useOfferings
};